import type {
  ProjectConfig,
  HeaderSetting,
  MenuSetting,
  TransitionSetting,
  MultiTabsSetting,
} from '/#/config';
import type { BeforeMiniState } from '/#/store';

import { defineStore } from 'pinia';
import { store } from '@/store';

import { ThemeEnum } from '@/enums/appEnum';
import { APP_DARK_MODE_KEY_, PROJ_CFG_KEY } from '@/enums/cacheEnum';
import { Persistent } from '@/utils/cache/persistent';
import { darkMode } from '@/settings/designSetting';
import { deepMerge } from '@/utils';
import {StorageUtils} from 'xmgoing-utils';

interface AppState {
  appObj:any,
  showRouterFlag:boolean;
  layout:string;
  pageName:string;
  darkMode?: ThemeEnum;
  // Page loading status
  pageLoading: boolean;
  activeSubId:string;
  // project config
  projectConfig: ProjectConfig | null;
  // When the window shrinks, remember some states, and restore these states when the window is restored
  beforeMiniInfo: BeforeMiniState;
}
let timeId: TimeoutHandle;
export const useAppStore = defineStore({
  id: 'app',
  state: (): AppState => ({
    showRouterFlag:true,
    pageName:'',
    darkMode: undefined,
    activeSubId:'',
    layout:StorageUtils.getItem("layout")||'defaultFrameCom',
    pageLoading: false,
    projectConfig: Persistent.getLocal(PROJ_CFG_KEY),
    beforeMiniInfo: {},
    appObj:{},
  }),
  getters: {
    notShowAppMenuFlag():boolean{
      if(this.projectConfig.notShowAppMenuFlag==null||this.projectConfig.notShowAppMenuFlag===""){
        return false;
      }else{
        return  this.projectConfig.notShowAppMenuFlag;
      }
    },
    getBaseFontSize():number{
      return  this.projectConfig.baseFontSize||12;
    },
    getBaseBorderRaduis():number{
      return  this.projectConfig.baseBorderRaduis||2;
    },
    getBaseSpace():number{
      return  this.projectConfig.baseSpace||12;
    },
    getLeftMenuWidth():number{
      return  this.projectConfig.leftMenuWidth||205;
    },
    getPagName(): string {
      return this.pageName||(StorageUtils.getSessionItem("curPageName")+'');
    },
    getPageLoading(): boolean {
      return this.pageLoading;
    },
    getQuickMenuShow(): boolean {
      return this.projectConfig.quickMenuFlag;
    },
    //打开多个菜单层级
    getMenuOpenMore(): boolean {
      return this.projectConfig.menuOpenMore;
    },
    getDarkMode(): 'light' | 'dark' | string {
      return this.darkMode || localStorage.getItem(APP_DARK_MODE_KEY_) || darkMode;
    },

    getBeforeMiniInfo(): BeforeMiniState {
      return this.beforeMiniInfo;
    },

    getProjectConfig(): ProjectConfig {
      if(this.projectConfig.userDefLayout==null){
        this.projectConfig.userDefLayout=true;
      }
      if(this.projectConfig.openKeepAlive==null){
        this.projectConfig.openKeepAlive=true;
      }
      return this.projectConfig || ({} as ProjectConfig);
    },
    showHelpe(): HeaderSetting {
      return this.getProjectConfig.headerSetting;
    },

    getHeaderSetting(): HeaderSetting {
      return this.getProjectConfig.headerSetting;
    },
    getMenuSetting(): MenuSetting {
      return this.getProjectConfig.menuSetting;
    },
    getTransitionSetting(): TransitionSetting {
      return this.getProjectConfig.transitionSetting;
    },
    getMultiTabsSetting(): MultiTabsSetting {
      return this.getProjectConfig.multiTabsSetting;
    },
    getActiveSubMenuId(){
      return this.activeSubId;
    },
    getLayout(){
      return this.layout;
    }
  },
  actions: {
    setActiveSubMenuId(activeSubId:string,pageName:string){
      this.activeSubId=activeSubId;
    },
    clearActiveSubMenu(){
      this.activeSubId="";
    },
    setPageName(pageName:string){
      this.pageName=pageName;
      StorageUtils.addSessionItem("curPageName",pageName);
    },
    setShowRouterFlag(showRouterFlag:boolean){
      this.showRouterFlag=showRouterFlag;
    },
    setAppObj(appObjTemp){
      this.appObj=appObjTemp;
    },
    setLayout(layout:string){
      this.layout=layout;
    },
    setPageLoading(loading: boolean): void {
      this.pageLoading = loading;
    },

    setDarkMode(mode: ThemeEnum): void {
      this.darkMode = mode;
      localStorage.setItem(APP_DARK_MODE_KEY_, mode);
    },

    setBeforeMiniInfo(state: BeforeMiniState): void {
      this.beforeMiniInfo = state;
    },

    setProjectConfig(config: DeepPartial<ProjectConfig>): void {
      // 使用新对象引用，避免 deepMerge 原地改写导致 Pinia/视图不刷新（切换框架时主题晚一拍）
      this.projectConfig = deepMerge({ ...(this.projectConfig || {}) }, config);
      Persistent.setLocal(PROJ_CFG_KEY, this.projectConfig);
    },

    async resetAllState() {
      Persistent.clearAll();
    },
    async setPageLoadingAction(loading: boolean): Promise<void> {
      if (loading) {
        clearTimeout(timeId);
        // Prevent flicker
        timeId = setTimeout(() => {
          this.setPageLoading(loading);
        }, 50);
      } else {
        this.setPageLoading(loading);
        clearTimeout(timeId);
      }
    },
  },
});

// Need to be used outside the setup
export function useAppStoreWithOut() {
  return useAppStore(store);
}
